import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, pushSupported } from "../lib/push";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Bell, ShieldCheck, Info, Wrench, BellRing, BellOff, MessageCircle, Check } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });

// Iter123k184 · chiave localStorage per ricordare l'ultimo filtro scelto dall'utente
const FILTER_STORAGE_KEY = "ginius_notif_filter";

// Iter123k184 · deriva l'URL target di una notifica (meta.url ha priorità, poi mapping classico)
function notificationUrl(n) {
  const meta = n?.meta || {};
  if (meta.url) return meta.url;
  if (meta.ticket_id) return `/segnalazioni/${meta.ticket_id}`;
  if (meta.preventivo_id) return `/preventivi/${meta.preventivo_id}`;
  if (meta.credius_id) return `/credius/${meta.credius_id}`;
  if (meta.sinistro_id) return "/miei-sinistri";
  if (typeof n.tipo === "string" && n.tipo.startsWith("avviso")) return "/bacheca";
  return null;
}

// Iter123k183 · Stato tri-colore per le notifiche actionable
const STATUS_META = {
  da_leggere: { label: "Da leggere", dot: "bg-red-600", ring: "ring-red-200", testid: "da-leggere" },
  attesa:     { label: "Attesa / Da rivedere", dot: "bg-amber-500", ring: "ring-amber-200", testid: "attesa" },
  letto:      { label: "Letto / Eseguito", dot: "bg-emerald-600", ring: "ring-emerald-200", testid: "letto" },
};

function StatusChip({ status, onClick, active, size = "sm" }) {
  const m = STATUS_META[status]; if (!m) return null;
  const dim = size === "lg" ? "w-6 h-6" : "w-5 h-5";
  return (
    <button type="button" onClick={onClick}
      data-testid={`notif-status-${m.testid}${active ? "-active" : ""}`}
      title={m.label}
      className={`${dim} rounded-full ${m.dot} inline-flex items-center justify-center text-white transition-transform ${active ? `ring-2 ${m.ring} scale-110` : "opacity-50 hover:opacity-100 hover:scale-105"}`}>
      <Check className="w-3 h-3" strokeWidth={3} />
    </button>
  );
}

export function NotificationBell({ variant = "dark" }) {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [chat, setChat] = useState({ preventivi: 0, manutenzioni: 0, sinistri: 0 });
  const [open, setOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  // Iter123k183/184 · filtro corrente ("" = tutte, oppure uno dei 3 stati) — persistito in localStorage
  const [filterStatus, setFilterStatus] = useState(() => {
    try { return localStorage.getItem(FILTER_STORAGE_KEY) || ""; } catch { return ""; }
  });
  const navigate = useNavigate();

  // Iter123k184 · ogni volta che l'utente cambia filtro, lo salvo
  const changeFilter = useCallback((val) => {
    setFilterStatus(val);
    try { localStorage.setItem(FILTER_STORAGE_KEY, val || ""); } catch {}
  }, []);

  const refreshPush = useCallback(() => { getPushStatus().then(setPushStatus); }, []);
  useEffect(() => { refreshPush(); }, [refreshPush]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus === "subscribed") {
        await disablePush();
        toast.success("Avvisi sul telefono disattivati");
      } else {
        await enablePush();
        toast.success("Avvisi sul telefono attivati! Riceverai una notifica per ogni nuova manutenzione o avviso.");
      }
      await refreshPush();
    } catch (e) {
      toast.error(e.message || "Impossibile aggiornare le notifiche push");
    } finally { setPushBusy(false); }
  };

  const load = useCallback(() => {
    api.get("/my-notifications").then((r) => setData(r.data)).catch(() => {});
    api.get("/me/messaggi-non-letti").then((r) => setChat(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = async (o) => {
    setOpen(o);
    // Iter123k183 · All'apertura segnamo "letto" solo le notifiche INFORMATIVE (senza `status`).
    // Le notifiche actionable richiedono spunta esplicita e NON vengono toccate.
    if (o && (data.notifications || []).some((n) => !("status" in n) && !n.read)) {
      await api.post("/my-notifications/read-all").catch(() => {});
      setData((d) => {
        const notifs = d.notifications.map((n) => "status" in n ? n : { ...n, read: true });
        // ricalcola unread
        const unread = notifs.reduce((acc, n) =>
          acc + (("status" in n) ? (n.status !== "letto" ? 1 : 0) : (!n.read ? 1 : 0)), 0);
        return { ...d, notifications: notifs, unread };
      });
    }
  };

  const setStatus = async (nid, newStatus) => {
    try {
      await api.post(`/my-notifications/${nid}/status`, { status: newStatus });
      setData((d) => {
        const notifs = d.notifications.map((n) => n.id === nid ? { ...n, status: newStatus, read: newStatus === "letto" } : n);
        const unread = notifs.reduce((acc, n) =>
          acc + (("status" in n) ? (n.status !== "letto" ? 1 : 0) : (!n.read ? 1 : 0)), 0);
        return { ...d, notifications: notifs, unread };
      });
    } catch (e) {
      toast.error("Impossibile aggiornare lo stato della notifica");
    }
  };

  // Iter123k184 · click sulla notifica → naviga alla pratica collegata e chiude il popover.
  // Ignora i click originati dai pulsanti di stato (già gestiti con stopPropagation).
  const openNotification = (n) => {
    const url = notificationUrl(n);
    if (!url) return;
    setOpen(false);
    navigate(url);
  };

  const triggerColor = variant === "dark" ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100";
  const chatTotal = (chat.preventivi || 0) + (chat.manutenzioni || 0) + (chat.sinistri || 0);
  const badgeTotal = (data.unread || 0) + chatTotal;

  // Iter123k183 · contatori per i filtri (solo tra le notifiche actionable)
  const actionableCounts = useMemo(() => {
    const c = { da_leggere: 0, attesa: 0, letto: 0, total: 0 };
    for (const n of (data.notifications || [])) {
      if ("status" in n) {
        c.total += 1;
        if (c[n.status] !== undefined) c[n.status] += 1;
      }
    }
    return c;
  }, [data.notifications]);

  const visibleNotifications = useMemo(() => {
    const list = data.notifications || [];
    if (!filterStatus) return list;
    return list.filter((n) => n.status === filterStatus);
  }, [data.notifications, filterStatus]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className={`relative rounded-lg p-2.5 transition-colors ${triggerColor}`} aria-label="Notifiche">
          <Bell className="w-6 h-6" />
          {badgeTotal > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-xs font-bold min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">
              {badgeTotal > 9 ? "9+" : badgeTotal}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[92vw] p-0">
        <div className="p-4 border-b font-bold text-lg flex items-center gap-2">
          <Bell className="w-5 h-5" /> Notifiche
        </div>
        {chatTotal > 0 && (
          <div data-testid="chat-unread-summary" className="p-3 border-b bg-[#FBEEF3] text-sm text-brand flex items-start gap-2">
            <MessageCircle className="w-5 h-5 shrink-0" />
            <span><b>{chatTotal}</b> messagg{chatTotal > 1 ? "i" : "io"} in chat non letto{chatTotal > 1 ? "i" : ""}
              {(chat.preventivi > 0 || chat.manutenzioni > 0) && ` (${[chat.manutenzioni > 0 ? `Manutenzioni: ${chat.manutenzioni}` : "", chat.preventivi > 0 ? `Preventivi: ${chat.preventivi}` : ""].filter(Boolean).join(" · ")})`}. Aprili nella relativa pratica per rispondere.</span>
          </div>
        )}
        {/* Iter123k183 · chip di filtro visibili solo se ci sono notifiche actionable */}
        {actionableCounts.total > 0 && (
          <div className="px-3 py-2 border-b flex items-center gap-1.5 flex-wrap bg-slate-50"
            data-testid="notif-filter-bar">
            <button type="button" data-testid="notif-filter-tutte"
              onClick={() => changeFilter("")}
              className={`text-[11px] font-bold px-2 py-0.5 rounded-full border transition-colors ${filterStatus === "" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
              Tutte · {actionableCounts.total}
            </button>
            {["da_leggere", "attesa", "letto"].map((st) => {
              const m = STATUS_META[st];
              const active = filterStatus === st;
              return (
                <button key={st} type="button" data-testid={`notif-filter-${m.testid}`}
                  onClick={() => changeFilter(active ? "" : st)}
                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
                  {actionableCounts[st]}
                </button>
              );
            })}
          </div>
        )}
        <div className="max-h-[60vh] overflow-y-auto">
          {visibleNotifications.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-base" data-testid="notif-empty">
              {filterStatus ? "Nessuna notifica per il filtro selezionato." : "Nessuna notifica."}
            </div>
          ) : (
            visibleNotifications.map((n) => {
              const tipoStr = typeof n.tipo === "string" ? n.tipo : "";
              const isSinistro = tipoStr === "sinistro_definito";
              const isSegn = tipoStr.startsWith("segnalazione");
              const Icon = isSinistro ? ShieldCheck : isSegn ? Wrench : Info;
              const color = isSinistro ? "text-green-700" : isSegn ? "text-brand" : "text-slate-600";
              const hasStatus = "status" in n;
              // Iter123k185 · tutti e 3 gli stati con sfondo colorato trasparente e distinguibile
              const bgClass = hasStatus
                ? (n.status === "letto" ? "bg-emerald-100/70"
                   : n.status === "attesa" ? "bg-amber-100/70"
                   : "bg-red-100/70")
                : (n.read ? "bg-white" : "bg-[#FBEEF3]");
              const url = notificationUrl(n);
              const clickable = !!url;
              return (
                <div key={n.id} data-testid={`notification-item-${n.id}`}
                  onClick={clickable ? () => openNotification(n) : undefined}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNotification(n); } } : undefined}
                  className={`p-4 border-b flex gap-3 ${bgClass} ${clickable ? "cursor-pointer hover:bg-slate-100/70 transition-colors" : ""}`}>
                  <Icon className={`w-6 h-6 shrink-0 ${color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-base">{n.title}</div>
                    <div className="text-sm text-slate-600 whitespace-pre-wrap">{n.body}</div>
                    <div className="text-xs text-slate-400 mt-1">{fmt(n.created_at)}</div>
                    {hasStatus && (
                      <div className="flex items-center gap-2 mt-2" data-testid={`notif-status-row-${n.id}`}
                        onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Stato:</span>
                        {["da_leggere", "attesa", "letto"].map((st) => (
                          <StatusChip key={st} status={st} active={n.status === st}
                            onClick={() => setStatus(n.id, st)} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {pushSupported() && pushStatus !== "denied" && (
          <button data-testid="push-toggle-button" onClick={togglePush} disabled={pushBusy}
            className="w-full p-3 border-t flex items-center justify-center gap-2 text-sm font-semibold text-brand hover:bg-[#FBEEF3] transition-colors disabled:opacity-50">
            {pushStatus === "subscribed" ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
            {pushBusy ? "Attendere..." : pushStatus === "subscribed" ? "Disattiva avvisi sul telefono" : "Attiva avvisi sul telefono"}
          </button>
        )}
        {pushSupported() && pushStatus === "denied" && (
          <div data-testid="push-denied-hint" className="p-3 border-t text-xs text-slate-500 text-center">
            Notifiche bloccate dal browser. Per riceverle, abilitale dalle impostazioni del sito.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
